import React, { useState, useEffect } from 'react';
import api from '../services/api';

const CustomerLifecycleDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [lifecycleData, setLifecycleData] = useState(null);
  const [formData, setFormData] = useState({});

  const tabs = {
    overview: '📊 Overview',
    onboarding: '🎯 Onboarding',
    retention: '🔄 Retention',
    loyalty: '💎 Loyalty Program',
    personalization: '🤖 Personalization',
    analytics: '📈 Analytics'
  };

  const journeyStages = [
    {
      id: 'awareness',
      title: 'Awareness',
      description: 'Customer discovers your brand',
      icon: '👁️',
      metrics: { visitors: '12.5K', conversion: '2.3%' },
      active: true
    },
    {
      id: 'consideration',
      title: 'Consideration',
      description: 'Customer evaluates your offering',
      icon: '🤔',
      metrics: { leads: '287', engagement: '45%' },
      active: true
    },
    {
      id: 'purchase',
      title: 'Purchase',
      description: 'Customer makes first purchase',
      icon: '💳',
      metrics: { conversions: '156', aov: '$127' },
      active: true
    },
    {
      id: 'onboarding',
      title: 'Onboarding',
      description: 'Customer learns to use product',
      icon: '🎓',
      metrics: { completion: '78%', satisfaction: '4.2/5' },
      active: true
    },
    {
      id: 'advocacy',
      title: 'Advocacy',
      description: 'Customer becomes brand advocate',
      icon: '📢',
      metrics: { referrals: '42', nps: '8.7' },
      active: false
    }
  ];

  const loyaltyProgram = {
    tiers: [
      { name: 'Bronze', members: 1250, benefits: 'Basic rewards', minSpend: 0 },
      { name: 'Silver', members: 890, benefits: 'Enhanced rewards + Early access', minSpend: 500 },
      { name: 'Gold', members: 420, benefits: 'Premium rewards + VIP support', minSpend: 1500 },
      { name: 'Platinum', members: 125, benefits: 'Exclusive rewards + Personal shopper', minSpend: 5000 }
    ],
    metrics: {
      totalMembers: 2685,
      activeMembers: 1847,
      monthlyRedemptions: 3420,
      avgLifetimeValue: 2850
    }
  };

  const personalizationInsights = {
    segments: [
      { name: 'Frequent Buyers', size: 342, engagement: 89, conversionLift: '+23%' },
      { name: 'Price Sensitive', size: 1205, engagement: 67, conversionLift: '+15%' },
      { name: 'Premium Seekers', size: 187, engagement: 95, conversionLift: '+45%' },
      { name: 'Seasonal Shoppers', size: 891, engagement: 73, conversionLift: '+18%' }
    ],
    campaigns: {
      active: 12,
      avgOpenRate: '34.2%',
      avgClickRate: '8.7%',
      revenueImpact: '+$127K'
    }
  };

  const analyticsData = {
    customerMetrics: [
      { label: 'Total Customers', value: '14,267', change: '+12%', trend: 'up' },
      { label: 'Active Customers (30d)', value: '8,934', change: '+8%', trend: 'up' },
      { label: 'New Customers (30d)', value: '1,123', change: '+15%', trend: 'up' },
      { label: 'Churned Customers', value: '234', change: '-5%', trend: 'down' }
    ],
    retentionMetrics: [
      { label: 'Day 1 Retention', value: '87%', change: '+3%', trend: 'up' },
      { label: 'Day 7 Retention', value: '64%', change: '+5%', trend: 'up' },
      { label: 'Day 30 Retention', value: '42%', change: '+2%', trend: 'up' },
      { label: 'Day 90 Retention', value: '28%', change: '-1%', trend: 'down' }
    ]
  };

  useEffect(() => {
    // Simulate API call
    const loadData = async () => {
      setLoading(true);
      try {
        // const response = await api.get('/customer-lifecycle/data');
        // setLifecycleData(response.data);
        setTimeout(() => setLoading(false), 1000);
      } catch (error) {
        console.error('Error loading lifecycle data:', error);
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          fontSize: '1.1rem',
          color: 'var(--gray-500)'
        }}>Loading Customer Lifecycle Dashboard...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => window.location.href = '/'}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Customer Lifecycle Management
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🔄 Customer Lifecycle Management
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Manage customer journey, retention, and loyalty programs
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '0',
            gap: 'var(--space-1)',
            overflowX: 'auto'
          }}>
        {Object.entries(tabs).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              background: 'none',
              border: 'none',
              borderBottom: `3px solid ${activeTab === key ? 'var(--primary-600)' : 'transparent'}`,
              cursor: 'pointer',
              fontWeight: '600',
              color: activeTab === key ? 'var(--primary-600)' : 'var(--gray-500)',
              transition: 'all 0.3s',
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </button>
        ))}
          </div>
        </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          <h2 style={{
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-semibold)'
          }}>Customer Journey Visualization</h2>
          
          {/* Journey Stages */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--space-6)',
            marginBottom: 'var(--space-10)'
          }}>
            {journeyStages.map((stage) => (
              <div 
                key={stage.id}
                className="card"
                style={{
                  borderLeft: `4px solid ${stage.active ? 'var(--primary-600)' : 'var(--gray-300)'}`,
                  opacity: stage.active ? 1 : 0.7
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                  <span style={{ fontSize: '1.5rem' }}>{stage.icon}</span>
                  <h3 style={{
                    color: 'var(--gray-900)',
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-semibold)',
                    margin: 0
                  }}>{stage.title}</h3>
                </div>
                
                <p style={{
                  color: 'var(--gray-600)',
                  marginBottom: 'var(--space-4)',
                  lineHeight: 1.6
                }}>{stage.description}</p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {Object.entries(stage.metrics).map(([key, value]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.9rem', color: 'var(--gray-500)', textTransform: 'capitalize' }}>
                        {key.replace(/([A-Z])/g, ' $1')}
                      </span>
                      <span style={{
                        fontSize: '0.95rem',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-900)'
                      }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Onboarding Tab */}
      {activeTab === 'onboarding' && (
        <div className="card">
          <h2 style={{
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-semibold)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>🎯 Customer Onboarding</h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 'var(--space-6)',
            marginBottom: 'var(--space-8)'
          }}>
            <div className="card" style={{ textAlign: 'center' }}>
              <h3 style={{ color: 'var(--gray-700)', fontSize: 'var(--font-size-sm)', textTransform: 'uppercase', margin: '0 0 var(--space-2) 0' }}>Completion Rate</h3>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-extrabold)', color: 'var(--primary-600)' }}>78%</div>
              <div style={{ color: 'var(--gray-500)', fontSize: '0.8rem' }}>+5% vs last month</div>
            </div>
            
            <div className="card" style={{ textAlign: 'center' }}>
              <h3 style={{ color: 'var(--gray-700)', fontSize: 'var(--font-size-sm)', textTransform: 'uppercase', margin: '0 0 var(--space-2) 0' }}>Avg Time to Complete</h3>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-extrabold)', color: 'var(--primary-600)' }}>4.2 days</div>
              <div style={{ color: 'var(--gray-500)', fontSize: '0.8rem' }}>-0.8 days vs last month</div>
            </div>
            
            <div className="card" style={{ textAlign: 'center' }}>
              <h3 style={{ color: 'var(--gray-700)', fontSize: 'var(--font-size-sm)', textTransform: 'uppercase', margin: '0 0 var(--space-2) 0' }}>Drop-off Rate</h3>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-extrabold)', color: 'var(--red-600)' }}>22%</div>
              <div style={{ color: 'var(--gray-500)', fontSize: '0.8rem' }}>-3% vs last month</div>
            </div>
          </div>

          <div>
            <h3 style={{ color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>Onboarding Steps Performance</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {[
                { step: 'Welcome Email', completion: 95, avgTime: '5 min' },
                { step: 'Profile Setup', completion: 87, avgTime: '12 min' },
                { step: 'First Purchase', completion: 78, avgTime: '2.3 days' },
                { step: 'Feature Tutorial', completion: 65, avgTime: '18 min' }
              ].map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px'
                }}>
                  <span style={{ fontWeight: 'var(--font-medium)' }}>{item.step}</span>
                  <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                    <span style={{ color: 'var(--primary-600)', fontWeight: 'var(--font-semibold)' }}>
                      {item.completion}%
                    </span>
                    <span style={{ color: 'var(--gray-500)', fontSize: '0.9rem' }}>
                      {item.avgTime}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Retention Tab */}
      {activeTab === 'retention' && (
        <div className="card">
          <h2 style={{
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-semibold)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>🔄 Customer Retention</h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-6)',
            marginBottom: 'var(--space-8)'
          }}>
            {analyticsData.retentionMetrics.map((metric, index) => (
              <div key={index} className="card" style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  color: 'var(--gray-700)', 
                  fontSize: 'var(--font-size-sm)', 
                  textTransform: 'uppercase', 
                  margin: '0 0 var(--space-2) 0' 
                }}>{metric.label}</h3>
                <div style={{ 
                  fontSize: 'var(--font-size-3xl)', 
                  fontWeight: 'var(--font-extrabold)', 
                  color: 'var(--primary-600)' 
                }}>{metric.value}</div>
                <div style={{ 
                  color: metric.trend === 'up' ? 'var(--green-500)' : 'var(--red-500)', 
                  fontSize: '0.8rem',
                  fontWeight: 'var(--font-medium)'
                }}>{metric.change}</div>
              </div>
            ))}
          </div>

          <div>
            <h3 style={{ color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>Retention Strategies</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {[
                { strategy: 'Welcome Email Series', effectiveness: 'High', impact: '+23% retention' },
                { strategy: 'Re-engagement Campaigns', effectiveness: 'Medium', impact: '+15% retention' },
                { strategy: 'Loyalty Point Bonuses', effectiveness: 'High', impact: '+31% retention' },
                { strategy: 'Personalized Offers', effectiveness: 'Very High', impact: '+42% retention' }
              ].map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px'
                }}>
                  <span style={{ fontWeight: 'var(--font-medium)' }}>{item.strategy}</span>
                  <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
                    <span className="badge badge-success" style={{ fontSize: '0.8rem' }}>
                      {item.effectiveness}
                    </span>
                    <span style={{ color: 'var(--primary-600)', fontWeight: 'var(--font-semibold)' }}>
                      {item.impact}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Loyalty Program Tab */}
      {activeTab === 'loyalty' && (
        <div>
          <h2 style={{
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-semibold)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>💎 Loyalty Program</h2>

          {/* Program Metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-6)',
            marginBottom: 'var(--space-8)'
          }}>
            {Object.entries(loyaltyProgram.metrics).map(([key, value]) => (
              <div key={key} className="card" style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  color: 'var(--gray-700)', 
                  fontSize: 'var(--font-size-sm)', 
                  textTransform: 'uppercase', 
                  margin: '0 0 var(--space-2) 0' 
                }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h3>
                <div style={{ 
                  fontSize: 'var(--font-size-3xl)', 
                  fontWeight: 'var(--font-extrabold)', 
                  color: 'var(--primary-600)' 
                }}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
              </div>
            ))}
          </div>

          {/* Loyalty Tiers */}
          <div className="card">
            <h3 style={{ color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>Loyalty Tiers</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--space-4)'
            }}>
              {loyaltyProgram.tiers.map((tier, index) => (
                <div key={index} style={{
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px',
                  borderLeft: `4px solid var(--primary-${(index + 3) * 100})`
                }}>
                  <h4 style={{ 
                    color: 'var(--gray-900)', 
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-semibold)'
                  }}>{tier.name}</h4>
                  <p style={{ color: 'var(--gray-600)', fontSize: '0.9rem', marginBottom: 'var(--space-3)' }}>
                    {tier.benefits}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: 'var(--gray-500)' }}>
                      {tier.members} members
                    </span>
                    <span style={{ color: 'var(--primary-600)', fontWeight: 'var(--font-medium)' }}>
                      ${tier.minSpend}+ spend
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Personalization Tab */}
      {activeTab === 'personalization' && (
        <div>
          <h2 style={{
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-semibold)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>🤖 AI Personalization</h2>

          {/* Campaign Metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-6)',
            marginBottom: 'var(--space-8)'
          }}>
            {Object.entries(personalizationInsights.campaigns).map(([key, value]) => (
              <div key={key} className="card" style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  color: 'var(--gray-700)', 
                  fontSize: 'var(--font-size-sm)', 
                  textTransform: 'uppercase', 
                  margin: '0 0 var(--space-2) 0' 
                }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h3>
                <div style={{ 
                  fontSize: 'var(--font-size-3xl)', 
                  fontWeight: 'var(--font-extrabold)', 
                  color: 'var(--primary-600)' 
                }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Customer Segments */}
          <div className="card">
            <h3 style={{ color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>Customer Segments</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {personalizationInsights.segments.map((segment, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px'
                }}>
                  <div>
                    <div style={{ fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-1)' }}>
                      {segment.name}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                      {segment.size} customers • {segment.engagement}% engagement
                    </div>
                  </div>
                  <div style={{ 
                    color: 'var(--green-600)', 
                    fontWeight: 'var(--font-semibold)',
                    fontSize: '1.1rem'
                  }}>
                    {segment.conversionLift}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div>
          <h2 style={{
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-semibold)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>📈 Analytics & Insights</h2>

          {/* Customer Metrics */}
          <div className="card" style={{ marginBottom: 'var(--space-8)' }}>
            <h3 style={{ color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>Customer Metrics</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--space-6)'
            }}>
              {analyticsData.customerMetrics.map((metric, index) => (
                <div key={index} style={{
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <h4 style={{ 
                    color: 'var(--gray-700)', 
                    fontSize: 'var(--font-size-sm)', 
                    textTransform: 'uppercase', 
                    margin: '0 0 var(--space-2) 0' 
                  }}>{metric.label}</h4>
                  <div style={{ 
                    fontSize: 'var(--font-size-2xl)', 
                    fontWeight: 'var(--font-extrabold)', 
                    color: 'var(--gray-900)',
                    marginBottom: 'var(--space-1)'
                  }}>{metric.value}</div>
                  <div style={{ 
                    color: metric.trend === 'up' ? 'var(--green-500)' : 'var(--red-500)', 
                    fontSize: '0.9rem',
                    fontWeight: 'var(--font-medium)'
                  }}>{metric.change}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Items */}
          <div className="card">
            <h3 style={{ color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>Recommended Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {[
                { 
                  action: 'Improve onboarding completion rate',
                  priority: 'High',
                  impact: 'Reduce churn by 12%',
                  effort: 'Medium'
                },
                { 
                  action: 'Launch win-back campaign for churned customers',
                  priority: 'Medium',
                  impact: 'Recover 8% of churned users',
                  effort: 'Low'
                },
                { 
                  action: 'Expand loyalty program benefits',
                  priority: 'Low',
                  impact: 'Increase LTV by 18%',
                  effort: 'High'
                }
              ].map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-1)' }}>
                      {item.action}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                      {item.impact} • {item.effort} effort
                    </div>
                  </div>
                  <span className={`badge ${
                    item.priority === 'High' ? 'badge-error' :
                    item.priority === 'Medium' ? 'badge-warning' : 'badge-gray'
                  }`}>
                    {item.priority}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CustomerLifecycleDashboard;